import {
	addDoc,
	collection,
	deleteDoc,
	doc,
	getDocs,
	updateDoc,
} from 'firebase/firestore'

import { BASE_COLLECTION, db } from 'config/firebase'
import { TabularData } from 'types/server/tabular'

import { PayphoneLocation } from './types'

const payphoneCollection = (orgId: string) =>
	collection(db, BASE_COLLECTION, orgId, 'payphone_data')

const toFirestorePayload = (location: PayphoneLocation) => {
	const pickupInstructions = (location.pickup_instructions || '').trim()
	return {
		location: {
			address: location.location.address.trim(),
			geometry: {
				lat: Number(location.location.geometry.lat),
				lng: Number(location.location.geometry.lng),
			},
			name: location.location.name.trim(),
		},
		name: location.name.trim(),
		phone_numbers: location.phone_numbers.map(n => n.trim()).filter(n => n !== ''),
		pickup_instructions: pickupInstructions,
		driver_instructions: pickupInstructions
			? `Customer told to wait at ${pickupInstructions}`
			: '',
	}
}

export const fetchPayphoneLocations = async (orgId: string): Promise<PayphoneLocation[]> => {
	const snapshot = await getDocs(payphoneCollection(orgId))
	return snapshot.docs.map(docSnap => {
		const data = docSnap.data() as Omit<PayphoneLocation, 'id'>
		return {
			id: docSnap.id,
			...data,
			pickup_instructions: data.pickup_instructions || '',
			phone_numbers: data.phone_numbers || [],
		}
	})
}

export const toPayphoneTable = (locations: PayphoneLocation[]): TabularData => ({
	table: {
		columns: [
			{ Header: 'Name', accessor: 'name' },
			{ Header: 'Phone Numbers', accessor: 'phone_numbers' },
		],
		rows: locations.map(location => ({
			route: location.id || '',
			name: location.name,
			phone_numbers: (location.phone_numbers || []).filter(Boolean).join(', '),
		})),
	},
})

export const createPayphoneLocation = async (orgId: string, location: PayphoneLocation) => {
	await addDoc(payphoneCollection(orgId), toFirestorePayload(location))
}

export const updatePayphoneLocation = async (orgId: string, location: PayphoneLocation) => {
	if (!location.id) {
		throw new Error('Missing payphone location id')
	}
	await updateDoc(
		doc(db, BASE_COLLECTION, orgId, 'payphone_data', location.id),
		toFirestorePayload(location)
	)
}

export const deletePayphoneLocation = async (orgId: string, id: string) => {
	await deleteDoc(doc(db, BASE_COLLECTION, orgId, 'payphone_data', id))
}
